import classNames from "classnames";
import {
  type ButtonHTMLAttributes,
  forwardRef,
  type ReactNode,
  type Ref,
} from "react";
import { Icon, type IconName } from "../Icon/Icon.tsx";
import { Spinner } from "../Spinner/Spinner.tsx";
import { BaseButton } from "./BaseButton.tsx";
import styles from "./button.module.css";

export type ButtonSize = "sm" | "md" | "lg";
export type ButtonVariant =
  | "primary"
  | "secondary"
  | "tertiary"
  | "quad"
  | "error"
  | "inverse-primary";

export type ButtonProps = {
  children?: ReactNode;
  variant?: ButtonVariant;
  loading?: boolean;
  loadingContent?: ReactNode;
  size?: ButtonSize;
  iconName?: IconName;
  iconPosition?: "left" | "right";
  active?: boolean;
} & ButtonHTMLAttributes<HTMLButtonElement>;

export const Button = forwardRef(
  (
    {
      children,
      loading,
      loadingContent,
      disabled,
      variant = "primary",
      type = "button",
      size = "md",
      iconName,
      iconPosition = "left",
      className,
      ...rest
    }: ButtonProps,
    ref: Ref<HTMLButtonElement>,
  ) => (
    <BaseButton
      ref={ref}
      className={classNames(styles[variant], styles[size], className)}
      type={type}
      disabled={!!disabled || loading}
      loading={loading}
      loadingContent={loadingContent ?? <Spinner size={size} />}
      children={
        <div className="flex-row items-center justify-center w-full gap-100">
          {iconName && iconPosition === "left" && (
            <Icon name={iconName} size={size !== "lg" ? "sm" : "md"} />
          )}
          {children ? (
            <div className="flex-row justify-center">{children}</div>
          ) : null}
          {iconName && iconPosition === "right" && (
            <Icon name={iconName} size={size !== "lg" ? "sm" : "md"} />
          )}
        </div>
      }
      {...rest}
    />
  ),
);
